import { LOCALE_COMMON_PATHS } from 'types/locales';

/**
 * Translates property type strings to localized versions for area page headings.
 * 
 * This handles the plural forms of property types used in headings like
 * "Houses For Sale" or "Condos For Rent", as well as the special case
 * of "Real Estate & Homes".
 * 
 * @param propertyType - The English property type string (e.g., "Houses", "Condos", "Real Estate & Homes")
 * @param t - Translation function from next-i18next
 * @returns Localized property type string
 * 
 * @example
 * translatePropertyType("Houses", t) // Returns "Maisons" in French
 * translatePropertyType("Real Estate & Homes", t) // Returns "Immobilier & Maisons" in French
 */
export function translatePropertyType(
  propertyType: string,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  // Special case for "Real Estate & Homes" - translate both parts separately
  if (propertyType === 'Real Estate & Homes') {
    const realEstate = t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_REAL_ESTATE, { defaultValue: 'Real Estate' });
    const homes = t(LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_HOMES, { defaultValue: 'Homes' });
    return `${realEstate} & ${homes}`;
  }

  // Map property types to their translation keys
  const typeMap: Record<string, string> = {
    Houses: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_HOUSES,
    Condos: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_CONDOS,
    Townhouses: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_TOWNHOUSES,
    Farms: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_FARMS,
    Commercial: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_COMMERCIAL,
    Land: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_LAND,
    Homes: LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_HOMES,
    'Real Estate': LOCALE_COMMON_PATHS.AREA_PAGE_PROPERTY_TYPES_REAL_ESTATE,
  };

  const translationKey = typeMap[propertyType];
  
  // If we have a translation key, use it; otherwise return the original
  return translationKey ? t(translationKey, { defaultValue: propertyType }) : propertyType;
}

